/**
 * Free goods for the offline pricing engine: which offers a draft earns, the reward lines they
 * write, the chips on the qualifying rows, and the provenance firings. Pure, like `priceOrder`.
 * Split from price-order.ts only for size; the rules are in docs/features/order-capture.md.
 */

import {
  DeliveryInput,
  FiringAllocation,
  LineOffer,
  PricedCell,
  PricedFiring,
  PricedLine,
  isSet,
} from './pricing.model';
import {
  FreeGoodEvaluationLevel,
  OfferRole,
  PricingDiscountList,
  PricingFreeGood,
  PricingItem,
} from '../../../interfaces/pricing/pricing-bundle.interface';
import type { LineDraft } from './price-order';

export interface FreeGoodsResult {
  /** Generated reward lines, one per rewarded product. */
  lines: PricedLine[];
  /** Offers keyed by the QUALIFYING product — the chip belongs on the row the rep is typing in. */
  offersByQualifier: Map<string, LineOffer[]>;
  firings: PricedFiring[];
}

/** Reward units earned per delivery sequence, with the rules that granted them. */
type EarnedRewards = Map<
  string,
  Map<number, { units: number; ruleIds: Set<string> }>
>;

/** A reward row with the product it gives: its own, or the single qualifier's when it names none. */
interface OfferReward {
  row: PricingFreeGood;
  product: string;
}

/** One numbered offer group: every qualifying row must be met, and each reward row gives on its own. */
interface OfferGroup {
  qualifiers: PricingFreeGood[];
  rewards: OfferReward[];
}

interface Qualifying {
  row: PricingFreeGood;
  draft: LineDraft | null;
}

interface RewardGiven {
  sequence: number;
  units: number;
  repeats: number;
}

interface FreeGoodNudge {
  qualifier: PricingFreeGood;
  away: number;
  at: { sequence?: number };
}

export function resolveFreeGoods(
  lists: PricingDiscountList[],
  drafts: LineDraft[],
  deliveries: DeliveryInput[],
  itemsByProduct: Map<string, PricingItem>,
  includeUnmapped: boolean,
): FreeGoodsResult {
  const draftByProduct = new Map<string, LineDraft>();
  for (const d of drafts) draftByProduct.set(d.input.product, d);

  const result: FreeGoodsAccumulator = {
    earned: new Map(),
    offersByQualifier: new Map(),
    firings: [],
  };
  for (const list of lists) {
    for (const group of offerGroups(list, includeUnmapped)) {
      const qualifying = group.qualifiers.map((row) => ({
        row,
        draft: draftByProduct.get(row.product) ?? null,
      }));
      if (qualifying.some((q) => q.draft))
        applyOfferGroup(list, group, qualifying, deliveries, result);
    }
  }
  return {
    lines: buildRewardLines(result.earned, itemsByProduct),
    offersByQualifier: result.offersByQualifier,
    firings: result.firings,
  };
}

interface FreeGoodsAccumulator {
  earned: EarnedRewards;
  offersByQualifier: Map<string, LineOffer[]>;
  firings: PricedFiring[];
}

/** Every reward row of one group: what it gives, its chip on each ordered qualifier, its nudges. */
function applyOfferGroup(
  list: PricingDiscountList,
  group: OfferGroup,
  qualifying: Qualifying[],
  deliveries: DeliveryInput[],
  result: FreeGoodsAccumulator,
): void {
  const ordered = qualifying.filter((q) => q.draft);
  for (const q of ordered)
    if (!result.offersByQualifier.has(q.row.product))
      result.offersByQualifier.set(q.row.product, []);
  const pushOffer = (product: string, offer: LineOffer) =>
    result.offersByQualifier.get(product)?.push(offer);

  for (const reward of group.rewards) {
    const { given, nudges } = evaluateReward(
      qualifying,
      reward.row,
      deliveries,
    );
    const units = given.reduce((sum, g) => sum + g.units, 0);
    if (units > 0) {
      for (const q of ordered)
        pushOffer(
          q.row.product,
          freeGoodOffer(q.row, reward, 'earned', null, units),
        );
      recordGiven(list, qualifying, reward, given, result);
    }
    for (const nudge of nudges) {
      pushOffer(nudge.qualifier.product, {
        ...freeGoodOffer(
          nudge.qualifier,
          reward,
          'available',
          nudge.away,
          reward.row.quantity,
        ),
        ...nudge.at,
      });
    }
  }
}

/**
 * Writes what a reward row gave. Split order records one firing per delivery that earned (FG-20).
 * A product not on the order gets its free line too, its unit from its price-list item (FG-09).
 */
function recordGiven(
  list: PricingDiscountList,
  qualifying: Qualifying[],
  reward: OfferReward,
  given: RewardGiven[],
  result: FreeGoodsAccumulator,
): void {
  for (const g of given)
    recordReward(result.earned, reward, g.sequence, g.units);
  const perDelivery =
    reward.row.evaluationLevel === FreeGoodEvaluationLevel.SplitOrder;
  for (const part of perDelivery ? given.map((g) => [g]) : [given])
    result.firings.push(freeGoodFiring(list, qualifying, reward, part));
}

/**
 * A free-goods firing gives units, not money: the qualifying lines that earned them (all of each
 * qualifier's deliveries, or only the ones that earned under split order) and the reward lines.
 */
function freeGoodFiring(
  list: PricingDiscountList,
  qualifying: Qualifying[],
  reward: OfferReward,
  given: RewardGiven[],
): PricedFiring {
  const perDelivery =
    reward.row.evaluationLevel === FreeGoodEvaluationLevel.SplitOrder;
  const earning = qualifying.flatMap((q) =>
    (q.draft?.cells ?? [])
      .filter(
        (cell) =>
          !perDelivery || given.some((g) => g.sequence === cell.sequence),
      )
      .map((cell) => ({
        product: q.row.product,
        sequence: cell.sequence,
        freeGood: false,
        role: 'Q' as const,
        quantity: cell.quantity,
        amount: 0,
      })),
  );
  const allocations: FiringAllocation[] = [
    ...earning,
    ...given.map((g) => ({
      product: reward.product,
      sequence: g.sequence,
      freeGood: true,
      role: 'R' as const,
      quantity: g.units,
      amount: 0,
    })),
  ];
  return {
    ruleId: null,
    freeGoodId: reward.row.id,
    listId: list.id,
    productId: reward.product,
    baseAmount: 0,
    amount: 0,
    uncappedAmount: null,
    evaluatedQuantity: earning.reduce((sum, a) => sum + a.quantity, 0),
    rewardQuantity: given.reduce((sum, g) => sum + g.units, 0),
    repeatCount: given.reduce((sum, g) => sum + g.repeats, 0),
    qualifyingRuleIds: [],
    qualifyingFreeGoodIds: qualifying.map((q) => q.row.id),
    allocations,
  };
}

/**
 * Offer groups that can be evaluated (web order grid parity). A group needs at least one
 * qualifying and one reward row; a row with no role is ignored, and a qualifier with no "buy"
 * makes the whole group unreadable. A reward naming no product gets the qualifier's, unless
 * there are several qualifiers to choose from.
 */
function offerGroups(
  list: PricingDiscountList,
  includeUnmapped: boolean,
): OfferGroup[] {
  const byNumber = new Map<number, PricingFreeGood[]>();
  for (const fg of list.freeGoods ?? []) {
    if (!isSet(fg.offerGroup)) continue;
    const members = byNumber.get(fg.offerGroup);
    if (members) members.push(fg);
    else byNumber.set(fg.offerGroup, [fg]);
  }

  const groups: OfferGroup[] = [];
  for (const members of byNumber.values()) {
    const qualifiers = members.filter((m) => m.role === OfferRole.Qualifying);
    if (!qualifiers.length || qualifiers.some((q) => !(q.quantity > 0)))
      continue;
    const rewards = rewardsOf(
      members,
      qualifiers.length === 1 ? qualifiers[0].product : null,
      includeUnmapped,
    );
    if (rewards.length) groups.push({ qualifiers, rewards });
  }
  return groups;
}

function rewardsOf(
  members: PricingFreeGood[],
  fallback: string | null,
  includeUnmapped: boolean,
): OfferReward[] {
  return (
    members
      .filter((m) => m.role === OfferRole.Reward && m.quantity > 0)
      // An unmapped reward has no catalogue row here, so it cannot be displayed or shipped.
      .filter((m) => !m.unmapped || includeUnmapped)
      .map((row) => ({ row, product: row.product || fallback }))
      .filter((reward): reward is OfferReward => !!reward.product)
  );
}

/**
 * Applies one reward row to its group's qualifiers. Every qualifier must reach its "buy"; the
 * smallest multiple across them is how often the reward is earned. Reports, per qualifier, how
 * far the nearest bucket is from the next reward.
 */
function evaluateReward(
  qualifying: Qualifying[],
  reward: PricingFreeGood,
  deliveries: DeliveryInput[],
): { given: RewardGiven[]; nudges: FreeGoodNudge[] } {
  const perDelivery =
    reward.evaluationLevel === FreeGoodEvaluationLevel.SplitOrder;
  const given: RewardGiven[] = [];
  // `maxRewardQuantity` caps the whole order, however many deliveries share it.
  let remaining = rewardCap(reward, qualifying);
  // Nearest bucket wins each qualifier's nudge: with a split order the rep only needs to top up one drop.
  const nearest = new Map<string, FreeGoodNudge>();

  for (const bucket of rewardBuckets(perDelivery, deliveries)) {
    const repeats = repeatsEarned(qualifying, reward, bucket.at);
    const units = Math.min(repeats * reward.quantity, remaining);
    if (units > 0) {
      remaining -= units;
      // The record states how often the offer was earned, even when the cap gave fewer (FG-19).
      given.push({ sequence: bucket.sequence, units, repeats });
    }

    // A non-repeatable offer already earned is done; otherwise every qualifier short of the next multiple is told.
    const target = repeats === 0 ? 1 : reward.isRepeatable ? repeats + 1 : null;
    if (target !== null)
      recordNudges(
        qualifying,
        target,
        bucket.at,
        perDelivery ? { sequence: bucket.sequence } : {},
        nearest,
      );
  }

  return { given, nudges: remaining > 0 ? [...nearest.values()] : [] };
}

/** Split order: every delivery on its own. Complete order: the whole order, landing in one delivery. */
function rewardBuckets(
  perDelivery: boolean,
  deliveries: DeliveryInput[],
): Array<{ sequence: number; at: number | null }> {
  if (perDelivery)
    return deliveries.map((d) => ({ sequence: d.sequence, at: d.sequence }));
  const landing = landingSequence(deliveries);
  return landing === null ? [] : [{ sequence: landing, at: null }];
}

/** The smallest multiple across every qualifier; one at most when the reward does not repeat. */
function repeatsEarned(
  qualifying: Qualifying[],
  reward: PricingFreeGood,
  at: number | null,
): number {
  const multiple = Math.min(
    ...qualifying.map((q) =>
      Math.floor(qualifyingUnits(q, at) / q.row.quantity),
    ),
  );
  return multiple > 0 && !reward.isRepeatable ? 1 : multiple;
}

/** A qualifier's units in one delivery, or across the order when `at` is null. */
function qualifyingUnits(q: Qualifying, at: number | null): number {
  return at === null
    ? (q.draft?.quantity ?? 0)
    : (q.draft?.cells.find((cell) => cell.sequence === at)?.quantity ?? 0);
}

/** Keeps, per ordered qualifier, the bucket closest to reaching `target` multiples. */
function recordNudges(
  qualifying: Qualifying[],
  target: number,
  at: number | null,
  place: { sequence?: number },
  nearest: Map<string, FreeGoodNudge>,
): void {
  for (const q of qualifying) {
    const away = target * q.row.quantity - qualifyingUnits(q, at);
    const known = nearest.get(q.row.id);
    if (q.draft && away > 0 && (!known || away < known.away))
      nearest.set(q.row.id, { qualifier: q.row, away, at: place });
  }
}

function rewardCap(reward: PricingFreeGood, qualifying: Qualifying[]): number {
  return rewardCapOf(
    reward,
    qualifying.map((q) => q.row),
  );
}

/** Read off the reward row first, then the qualifying rows (the tightest of those set). */
export function rewardCapOf(
  reward: PricingFreeGood,
  qualifiers: readonly PricingFreeGood[],
): number {
  if (isSet(reward.maxRewardQuantity)) return reward.maxRewardQuantity;
  const caps = qualifiers.map((q) => q.maxRewardQuantity).filter(isSet);
  return caps.length ? Math.min(...caps) : Infinity;
}

function recordReward(
  earned: EarnedRewards,
  reward: OfferReward,
  sequence: number,
  units: number,
): void {
  const bySequence =
    earned.get(reward.product) ??
    new Map<number, { units: number; ruleIds: Set<string> }>();
  const cell = bySequence.get(sequence) ?? {
    units: 0,
    ruleIds: new Set<string>(),
  };
  cell.units += units;
  cell.ruleIds.add(reward.row.id);
  bySequence.set(sequence, cell);
  earned.set(reward.product, bySequence);
}

function freeGoodOffer(
  qualifier: PricingFreeGood,
  reward: OfferReward,
  status: LineOffer['status'],
  away: number | null,
  rewardUnits: number,
): LineOffer {
  return {
    kind: 'freeGood',
    status,
    ruleId: reward.row.id,
    measure: 'quantity',
    threshold: qualifier.quantity,
    away,
    percentage: null,
    rewardProduct: reward.product,
    rewardUnits,
  };
}

/** One generated reward line per rewarded product, cells in sequence order. */
function buildRewardLines(
  earned: EarnedRewards,
  itemsByProduct: Map<string, PricingItem>,
): PricedLine[] {
  const result: PricedLine[] = [];
  for (const [product, bySequence] of earned) {
    const item = itemsByProduct.get(product) ?? null;
    const cells: PricedCell[] = [...bySequence.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([sequence, cell]) => ({
        sequence,
        quantity: cell.units,
        gross: 0,
        net: 0,
      }));
    const ruleIds = new Set<string>();
    for (const cell of bySequence.values())
      for (const id of cell.ruleIds) ruleIds.add(id);
    const units = cells.reduce((sum, c) => sum + c.quantity, 0);

    result.push({
      product,
      priceListItemId: item?.id ?? null,
      productCategory: item?.productCategory ?? null,
      unitPrice: item?.unitPrice ?? 0,
      quantity: units,
      perDelivery: cells,
      gross: 0,
      volumeDiscountPct: 0,
      manualDiscountPct: 0,
      effectiveDiscountPct: 100,
      net: 0,
      freeUnits: units,
      isFreeGood: true,
      appliedRuleIds: [...ruleIds].sort(),
      offers: [],
    });
  }
  return result.sort((a, b) => a.product.localeCompare(b.product));
}

/**
 * Complete-order rewards land in the primary delivery, or the first one when none is primary,
 * wherever they were earned. No delivery, nowhere to put them.
 */
export function landingSequence(
  deliveries: readonly DeliveryInput[],
): number | null {
  return (
    (deliveries.find((d) => d.isPrimary) ?? deliveries[0])?.sequence ?? null
  );
}
